import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  LogOut, Mail, Phone, Trash2, MessageSquare, Users, Inbox, BarChart3, Loader2,
  ChevronRight, Bot, User as UserIcon, ExternalLink, ShieldAlert,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmt(d) {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleString();
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const { toast } = useToast();

  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionMsgs, setSessionMsgs] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (!user.is_admin) return;
    (async () => {
      try {
        const [s, l, c] = await Promise.all([
          axios.get(`${API}/admin/stats`, { withCredentials: true }),
          axios.get(`${API}/admin/contacts`, { withCredentials: true }),
          axios.get(`${API}/admin/chat-sessions`, { withCredentials: true }),
        ]);
        setStats(s.data);
        setLeads(l.data);
        setSessions(c.data);
      } catch (e) {
        toast({ title: "Failed to load data", description: e?.response?.data?.detail || "Try again", variant: "destructive" });
      } finally {
        setBusy(false);
      }
    })();
  }, [user, loading, toast]);

  const loadSessionMessages = async (sid) => {
    setActiveSession(sid);
    setSessionMsgs([]);
    try {
      const { data } = await axios.get(`${API}/admin/chat-sessions/${sid}/messages`, {
        withCredentials: true,
      });
      setSessionMsgs(data);
    } catch (e) {
      toast({ title: "Couldn't load messages", variant: "destructive" });
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { withCredentials: true });
      setLeads((rows) => rows.filter((r) => r.id !== id));
      toast({ title: "Lead deleted" });
    } catch (e) {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-600">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/admin/login" replace />;

  if (!user.is_admin) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 shadow">
          <ShieldAlert className="h-10 w-10 text-amber-500" />
          <h2 className="mt-3 text-xl font-bold text-slate-900">Access denied</h2>
          <p className="mt-2 text-sm text-slate-600">
            You&rsquo;re signed in as <strong>{user.email}</strong> but this account isn&rsquo;t
            on the admin allowlist.
          </p>
          <Button onClick={logout} className="mt-5 w-full bg-slate-900 hover:bg-slate-800">
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-slate-900 text-white grid place-items-center font-bold">
              4<span className="text-cyan-400">11</span>
            </div>
            <div className="leading-tight">
              <div className="font-bold text-slate-900 text-[15px]">411TechSavvy</div>
              <div className="text-[10px] uppercase tracking-widest text-cyan-700">Admin Dashboard</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </Link>
            <div className="flex items-center gap-2">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-slate-200" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-cyan-600 text-white grid place-items-center text-xs font-bold">
                  {user.name?.[0] || user.email?.[0] || "A"}
                </div>
              )}
              <div className="hidden md:block leading-tight">
                <div className="text-sm font-semibold text-slate-900">{user.name || user.email}</div>
                <div className="text-xs text-slate-500">{user.email}</div>
              </div>
            </div>
            <Button onClick={logout} variant="outline" className="border-slate-300">
              <LogOut className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Leads" value={stats?.leads ?? ""} icon={Inbox} />
          <StatCard label="Chat Sessions" value={stats?.chat_sessions ?? ""} icon={Users} />
          <StatCard label="Chat Messages" value={stats?.chat_messages ?? ""} icon={MessageSquare} />
          <StatCard
            label="Email Alerts"
            value={stats?.email_enabled ? "Active" : "Off"}
            icon={Mail}
            tone={stats?.email_enabled ? "good" : "warn"}
          />
        </div>

        <div className="mt-8">
          <Tabs defaultValue="leads">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger>
              <TabsTrigger value="chats">Chat Sessions ({sessions.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="leads" className="mt-4">
              <LeadsTable leads={leads} onDelete={deleteLead} busy={busy} />
            </TabsContent>

            <TabsContent value="chats" className="mt-4">
              <ChatSessionsView
                sessions={sessions}
                activeSession={activeSession}
                onSelect={loadSessionMessages}
                messages={sessionMsgs}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  const toneCls =
    tone === "good"
      ? "bg-emerald-50 text-emerald-700"
      : tone === "warn"
      ? "bg-amber-50 text-amber-700"
      : "bg-cyan-50 text-cyan-700";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</div>
        <div className={`h-8 w-8 rounded-lg grid place-items-center ${toneCls}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-3 text-2xl font-extrabold text-slate-900">{String(value)}</div>
    </div>
  );
}

function LeadsTable({ leads, onDelete, busy }) {
  if (busy) return <div className="py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (!leads.length)
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
        No leads yet. They&rsquo;ll show up here when someone submits the contact form.
      </div>
    );
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Message</th>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-900">{l.name}</td>
                <td className="px-4 py-3">
                  <a href={`mailto:${l.email}`} className="text-cyan-700 hover:underline">{l.email}</a>
                </td>
                <td className="px-4 py-3">
                  <a href={`tel:${l.phone}`} className="text-slate-700 hover:text-cyan-700">{l.phone}</a>
                </td>
                <td className="px-4 py-3 max-w-xs">
                  <div className="line-clamp-2 text-slate-700">{l.message}</div>
                </td>
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onDelete(l.id)}
                    className="text-slate-400 hover:text-red-600 p-1"
                    aria-label="Delete lead"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChatSessionsView({ sessions, activeSession, onSelect, messages }) {
  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-4">
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
          Sessions
        </div>
        <div className="max-h-[560px] overflow-y-auto">
          {sessions.length === 0 && (
            <div className="p-6 text-sm text-slate-500">No chat sessions yet.</div>
          )}
          {sessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => onSelect(s.session_id)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${
                activeSession === s.session_id ? "bg-cyan-50" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-900 text-sm truncate">{s.session_id}</div>
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </div>
              <div className="text-xs text-slate-500 mt-1 line-clamp-1">
                <span className="font-medium">{s.last_role === "user" ? "User: " : "Bot: "}</span>
                {s.last_message}
              </div>
              <div className="flex items-center justify-between mt-1">
                <div className="text-[10px] text-slate-400">{fmt(s.last_time)}</div>
                <div className="text-[10px] text-slate-500">{s.message_count} msgs</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 min-h-[300px]">
        {!activeSession ? (
          <div className="h-full grid place-items-center text-sm text-slate-500">
            Select a session to view messages.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-slate-500">Session: <span className="font-mono">{activeSession}</span></div>
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role !== "user" && (
                  <div className="h-8 w-8 rounded-full bg-cyan-600 text-white grid place-items-center flex-shrink-0">
                    <Bot className="h-4 w-4" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-slate-900 text-white rounded-br-sm"
                      : "bg-slate-100 text-slate-800 rounded-bl-sm"
                  }`}
                >
                  {m.text}
                  <div className={`mt-1 text-[10px] ${m.role === "user" ? "text-slate-300" : "text-slate-500"}`}>
                    {fmt(m.created_at)}
                  </div>
                </div>
                {m.role === "user" && (
                  <div className="h-8 w-8 rounded-full bg-slate-200 text-slate-700 grid place-items-center flex-shrink-0">
                    <UserIcon className="h-4 w-4" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
